import React, { useState, useRef, useEffect } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { useToast } from "../hooks/use-toast";
import { MessageCircle, Send, Bot, User, X, Trash2 } from "lucide-react";
import { Textarea } from "./ui/textarea";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRobot } from "@fortawesome/free-solid-svg-icons";

interface QAMessage {
  id: string;
  type: "user" | "assistant";
  content: string;
  timestamp: Date;
  category?: string;
}

interface FloatingChatProps {
  groqApiKey?: string;
  isOpen?: boolean;
  onClose?: () => void;
}

const FloatingChat: React.FC<FloatingChatProps> = ({
  groqApiKey,
  isOpen: controlledIsOpen,
  onClose,
}) => {
  const { toast } = useToast();

  // Debug: Log API key availability
  useEffect(() => {
    console.log("FloatingChat: groqApiKey provided:", !!groqApiKey);
    console.log("FloatingChat: API key length:", groqApiKey?.length || 0);
  }, [groqApiKey]);

  const [isOpen, setIsOpen] = useState(controlledIsOpen ?? false);

  // Handle controlled vs uncontrolled state
  useEffect(() => {
    if (controlledIsOpen !== undefined) {
      setIsOpen(controlledIsOpen);
    }
  }, [controlledIsOpen]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      setIsOpen(false);
    }
  };

  const [question, setQuestion] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const initialMessage: QAMessage = {
    id: "1",
    type: "assistant",
    content: "Hello! 👋 I am here to help with environmental questions!",
    timestamp: new Date(),
    category: "greeting",
  };

  const [conversation, setConversation] = useState<QAMessage[]>([
    initialMessage,
  ]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [conversation, isOpen]);

  // AI response generation (same as AskMyhu component)
  const generateAIResponse = async (
    userQuestion: string,
    conversationHistory: QAMessage[],
  ): Promise<string> => {
    console.log(
      "FloatingChat: generateAIResponse called with API key:",
      !!groqApiKey,
    );

    if (!groqApiKey) {
      console.log(
        "FloatingChat: No API key provided, returning fallback message",
      );
      return "I'm here to help with environmental questions! However, AI functionality requires proper configuration. You can still browse our Knowledge Hub for comprehensive environmental information.";
    }

    try {
      console.log("FloatingChat: Making API request to Groq...");
      const recentHistory = conversationHistory.slice(-6);
      const messages = [
        {
          role: "system",
          content: `You are Myǃhū's friendly AI assistant, an expert in environmental science and sustainability. 

Your knowledge areas include:
- Environmental monitoring and data interpretation
- Climate change and adaptation strategies
- Biodiversity conservation
- Sustainable lifestyle practices
- Carbon footprint reduction
- Afro-ecological and community environmental knowledge
- Water and soil conservation
- Renewable energy solutions

Guidelines:
- Be friendly, helpful, and encouraging
- Provide practical, actionable advice
- Use clear, accessible language
- Keep responses concise (100-200 words)
- Encourage sustainable practices
- If you don't know something specific, direct users to our Knowledge Hub

Always maintain a warm, supportive tone as you're representing Myǃhū's mission of environmental stewardship.`,
        },
      ];

      recentHistory.forEach((msg) => {
        if (msg.type === "user") {
          messages.push({ role: "user", content: msg.content });
        } else if (msg.type === "assistant" && msg.category !== "greeting") {
          messages.push({ role: "assistant", content: msg.content });
        }
      });

      messages.push({ role: "user", content: userQuestion });

      const response = await fetch(
        "https://api.groq.com/openai/v1/chat/completions",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${groqApiKey}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model: "llama-3.1-8b-instant",
            messages: messages,
            max_tokens: 400,
            temperature: 0.7,
            top_p: 0.9,
            stream: false,
          }),
        },
      );

      console.log("FloatingChat: API response status:", response.status);

      if (!response.ok) {
        const errorData = await response.text();
        console.error(
          "FloatingChat: Groq API error:",
          response.status,
          errorData,
        );
        throw new Error(`API request failed with status ${response.status}`);
      }

      const data = await response.json();
      console.log("FloatingChat: API response received successfully");

      if (data.choices && data.choices[0] && data.choices[0].message) {
        return data.choices[0].message.content.trim();
      } else {
        throw new Error("Invalid response format from Groq API");
      }
    } catch (error) {
      console.error("FloatingChat: Error generating AI response:", error);
      return "I'm experiencing some technical difficulties right now. Please try asking your question again, or explore our Knowledge Hub for environmental information and guidance.";
    }
  };

  // Sanitize user input to prevent prompt injection attacks
  const sanitizeUserInput = (input: string): string => {
    if (!input || typeof input !== "string") return "";

    return input
      .trim()
      .replace(/[<>]/g, "") // Remove angle brackets
      .replace(/javascript:/gi, "") // Remove javascript: protocol
      .replace(/data:/gi, "") // Remove data: protocol
      .replace(/vbscript:/gi, "") // Remove vbscript: protocol
      .replace(/on\w+\s*=/gi, "") // Remove event handlers
      .substring(0, 500); // Reasonable length limit
  };

  const handleSubmitQuestion = async (predefinedQuestion?: string) => {
    const currentQuestion = predefinedQuestion || question;
    if (!currentQuestion.trim()) return;

    // Sanitize the user input
    const sanitizedQuestion = sanitizeUserInput(currentQuestion);
    if (!sanitizedQuestion) {
      return;
    }

    const userMessage: QAMessage = {
      id: Date.now().toString(),
      type: "user",
      content: sanitizedQuestion,
      timestamp: new Date(),
    };

    setConversation((prev) => [...prev, userMessage]);
    setIsLoading(true);
    setQuestion("");

    try {
      const aiResponse = await generateAIResponse(
        sanitizedQuestion,
        conversation,
      );

      const assistantMessage: QAMessage = {
        id: (Date.now() + 1).toString(),
        type: "assistant",
        content: aiResponse,
        timestamp: new Date(),
      };

      setConversation((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error("Error in handleSubmitQuestion:", error);

      const errorMessage: QAMessage = {
        id: (Date.now() + 1).toString(),
        type: "assistant",
        content:
          "I'm having trouble processing your question right now. Please try again in a moment, or browse our Knowledge Hub for immediate access to environmental information.",
        timestamp: new Date(),
      };

      setConversation((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmitQuestion();
    }
  };

  const quickResponses = [
    "What is a carbon footprint?",
    "How can I reduce plastic use?",
    "Explain sustainable agriculture.",
    "Tell me about renewable energy.",
  ];

  const handleClearChat = () => {
    setConversation([initialMessage]);
  };

  if (!isOpen) {
    // Render nothing if the chat is closed.
    // The launcher button is now in TopBar.tsx for controlled mode
    if (controlledIsOpen !== undefined) {
      return null;
    }

    return (
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          onClick={() => setIsOpen(true)}
          className="bg-[#40e0d0] hover:bg-[#40e0d0]/80 text-black rounded-full w-14 h-14 shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center"
        >
          <MessageCircle className="h-6 w-6" />
        </Button>
        {/* Notification badge 
        <div className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
          1
        </div>
        */}
      </div>
    );
  }

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/20 backdrop-blur-sm z-40 transition-opacity ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={handleClose}
      ></div>
      <div
        className={`fixed top-0 right-0 h-screen w-[480px] bg-background border-l shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="flex flex-col h-full bg-background">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center gap-2">
              <Bot className="w-6 h-6 text-primary" />
              <h2 className="text-lg font-semibold">AI Assistant</h2>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClearChat}
                aria-label="Clear Chat"
              >
                <Trash2 className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClose}
                aria-label="Close chat"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 p-4 overflow-y-auto">
            <div className="flex flex-col gap-4">
              {conversation.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex items-start gap-3 ${msg.type === "user" ? "justify-end" : "justify-start"}`}
                >
                  {msg.type === "assistant" && (
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground flex-shrink-0">
                      <Bot className="w-5 h-5" />
                    </div>
                  )}
                  <div
                    className={`max-w-sm p-3 rounded-lg ${
                      msg.type === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted"
                    }`}
                  >
                    <p className="text-sm">{msg.content}</p>
                  </div>
                  {msg.type === "user" && (
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5" />
                    </div>
                  )}
                </div>
              ))}
              {isLoading && (
                <div className="flex items-start gap-3 justify-start">
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground flex-shrink-0">
                    <Bot className="w-5 h-5" />
                  </div>
                  <div className="max-w-sm p-3 rounded-lg bg-muted">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-primary rounded-full animate-pulse delay-0"></span>
                      <span className="w-2 h-2 bg-primary rounded-full animate-pulse delay-150"></span>
                      <span className="w-2 h-2 bg-primary rounded-full animate-pulse delay-300"></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input Area */}
          <div className="p-4 border-t">
            {conversation.length <= 1 && (
              <div className="mb-4">
                <p className="text-sm text-muted-foreground mb-2">
                  Or try one of these suggestions:
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {quickResponses.map((text) => (
                    <Button
                      key={text}
                      variant="outline"
                      size="sm"
                      onClick={() => handleSubmitQuestion(text)}
                    >
                      {text}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            <div className="relative">
              <Textarea
                placeholder="Ask an environmental question..."
                className="pr-16 min-h-[40px] resize-none"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyPress={handleKeyPress}
                rows={1}
              />
              <Button
                type="submit"
                size="icon"
                className="absolute top-1/2 right-2 -translate-y-1/2"
                onClick={() => handleSubmitQuestion()}
                disabled={isLoading || !question.trim()}
                aria-label="Send message"
              >
                <Send className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FloatingChat;
